import React, { useContext, useEffect, useState } from "react";
import { LARAVEL_APP_HOST, SCREEN_PATHS } from "../../../constants";
import { Box, Button, Typography } from "@mui/material";
import { useAuthContext } from "../../../AuthContextWrapper";
import { useNavigate } from "react-router";
import { ProbateDetails } from "../../../models/ProbateDetails";
import { ProbatePacketCard } from "./ProbatePacketCard";
import { ScreenContentWrapper } from "../../shared/ScreenContentWrapper";
import { Estate } from "../../../models/Estate";
import { estateApi, probateApi } from "../../../apis";
import { Loading } from "../../shared/Loading";
import { HelpDialog } from "../../shared/HelpDialog";
import handLightBulb15 from "../../assets/hand-light-bulb-1.5x.png";
import handLightBulb2 from "../../assets/hand-light-bulb-2x.png";
import handLightBulb3 from "../../assets/hand-light-bulb-3x.png";
import handLightBulb1 from "../../assets/hand-light-bulb-1x.png";

export const ConfirmScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { activeEstate, user, refreshEstates, subscription } =
    useAuthContext()
  const [probateDetails, setProbateDetails] = useState<ProbateDetails>();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    getProbateDetails();
  }, []);

  const getProbateDetails = async (): Promise<void> => {
    if (activeEstate?.id) {
      const probateDetails = await probateApi.getProbateDetails(
        activeEstate?.id
      );
      if (probateDetails?.probateType) {
        setProbateDetails(probateDetails);
      } else {
        navigate(SCREEN_PATHS.probate);
      }
    }
  };

  const handleSubmit = async () => {
    // save this probate type to the estate
    if (!subscription?.isActive) {
      // ask to upgrade if they haven't paid
      setUpgradeOpen(true);
    } else {
      if (probateDetails?.probateType) {
        const estate = activeEstate || new Estate();
        try {
          if (estate.id === undefined) {
            throw new Error("could not save data, no estate found");
          }
          if (
            !probateDetails?.probateType?.id ||
            probateDetails?.probateType?.id === undefined
          ) {
            throw new Error("could not save data, no probate type found");
          }

          await estateApi.updateEstate(estate.id, {
            probate_type_id: probateDetails.probateType.id,
          });
          // estate.probate = estateProbate;
          // setActiveEstate(estate); // update the client-side data store
          refreshEstates?.();
        } catch (error) {
          throw new Error("could not save data");
        }
        navigate(SCREEN_PATHS.packetForms);
      } else {
        console.error("haven't determined probate version yet");
      }
    }
  };

  const goBack = () => {
    navigate(SCREEN_PATHS.probate);
  };

  const getUpgradeContent = (): JSX.Element => {
    return (
      <Box
        sx={{
          textAlign: "center",
        }}
      >
        <img
          srcSet={`${handLightBulb15} 1.5x, ${handLightBulb2} 2x, ${handLightBulb3} 3x`}
          src={handLightBulb1}
          alt="hand light bulb"
          title="hand light bulb"
        />
        <Typography
          variant="h3"
          sx={{
            py: 2,
            px: {
              sm: "150px",
              xs: 1,
            },
          }}
        >
          Upgrade to a paid plan to access this premium feature.
        </Typography>
      </Box>
    );
  };

  // when sending users to payment screen, we want them be redirected back to /app/forms-and-files when complete
  const redirectToAfterUpgrade = encodeURIComponent(
    window.location.protocol +
      "//" +
      window.location.host +
      // "/app/forms-and-files"
      "/app/forms-and-files/probate/confirm"
  );

  return (
    <ScreenContentWrapper
      pageSuffix="Confirm Probate"
      title="Get your Probate packet"
      completedAll={true}
      totalSteps={3}
      showBeta={true}
    >
      <>
        <HelpDialog
          open={upgradeOpen}
          content={getUpgradeContent()}
          onDeny={() => setUpgradeOpen(false)}
          denyText="No thanks, I'd like to continue doing this on my own"
          denyType="text"
          confirmText="Explore Plans"
          onConfirm={() =>
            (window.location.href = `${LARAVEL_APP_HOST}/payment-plan?redirectToAfterUpgrade=${redirectToAfterUpgrade}`)
          }
        />
        <Typography variant="h5" sx={{ my: 3 }}>
          We did it
          {user?.first ? `, ${user.first}` : ""}! Based on your answers, here’s
          the Probate Packet you’ll need
        </Typography>

        {!probateDetails?.probateType?.id ? (
          <Loading />
        ) : (
          <>
            <ProbatePacketCard probate={probateDetails?.probateType} />
            {/* HIDE BULLETED DESCRIPTION FOR NOW */}
            {/*<Typography sx={{ mt: 3 }}>*/}
            {/*  To continue with this packet, please confirm that the following*/}
            {/*  information you have provided is true to the best of your knowledge:*/}
            {/*</Typography>*/}
            {/*<ul>*/}
            {/*  <li>*/}
            {/*    <Typography>*/}
            {/*      *** Fair market value of the estate is less than $255,000 ****/}
            {/*    </Typography>*/}
            {/*  </li>*/}
            {/*  <li>*/}
            {/*    <Typography>*/}
            {/*      *** e.g. You are the sole surviving spouse and there are no other*/}
            {/*      beneficiaries ****/}
            {/*    </Typography>*/}
            {/*  </li>*/}
            {/*  <li>*/}
            {/*    <Typography>*/}
            {/*      *** Lorem ipsum dolor sit amet, consectetur adipiscing elit ****/}
            {/*    </Typography>*/}
            {/*  </li>*/}
            {/*</ul>*/}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Button variant="contained" onClick={handleSubmit}>
                Continue with this packet
              </Button>
              <Button onClick={goBack} sx={{ mt: 2 }}>
                Go back and edit my answers
              </Button>
            </Box>
          </>
        )}
      </>
    </ScreenContentWrapper>
  );
};
